import {
  createMutationKeys,
  createQueryKeys,
  mergeQueryKeys,
} from '@lukemorales/query-key-factory'
import { RequestTypes } from '@core/react-query/features/organisations/organisation/requestTypes'
import { APIAxiosInstance } from '@core/utils/api-axios-instance'
import { createQueryKeyString } from '@core/react-query/queryParams'

const organisationQueryKeys = createQueryKeys('organisation', {
  byId: ({ organisationId }: RequestTypes['getOrganisation']) => {
    return {
      queryKey: [organisationId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `organisations/${organisationId}`,
        )

        return data
      },
      contextQueries: {
        organisationUsers: ({
          term,
          size,
          page,
          roles,
          memberStatus,
          projectIds,
          companyIds,
          includeKioskMembers,
        }: RequestTypes['getOrganisationUsers']) => ({
          queryKey: [
            page,
            size,
            term,
            roles,
            memberStatus,
            projectIds,
            companyIds,
            includeKioskMembers,
          ],
          queryFn: async () => {
            const queryString = createQueryKeyString({
              page,
              size,
              term,
              roles,
              memberStatus,
              projectIds,
              companyIds,
              includeKioskMembers,
            })
            const { data } = await APIAxiosInstance.get(
              `organisations/${organisationId}/users${queryString}`,
            )
            return data
          },
        }),
        organisationUser: ({ userId }) => ({
          queryKey: [userId],
          queryFn: async () => {
            const { data } = await APIAxiosInstance.get(
              `organisations/${organisationId}/users/${userId}`,
            )
            return data
          },
        }),
        organisationDashboard: ({
          projectIds,
          templateIds,
          processTagIds,
        }: RequestTypes['getOrganisationDashboard']) => ({
          queryKey: [projectIds, templateIds, processTagIds],
          queryFn: async () => {
            const queryString = createQueryKeyString({
              projectIds,
              templateIds,
              processTagIds,
            })

            const { data } = await APIAxiosInstance.get(
              `organisations/${organisationId}/dashboard${queryString}`,
            )
            return data
          },
        }),
      },
    }
  },
  list: (payload: RequestTypes['getOrganisations']) => ({
    queryKey: [
      payload.page,
      payload.size,
      payload.term,
      payload.isTestOrganisation,
      payload.isVisibleTestOrganisation,
    ],
    queryFn: async ({ pageParam }) => {
      const queryString = createQueryKeyString({
        ...payload,
        page: pageParam ?? payload.page ?? 1,
      })
      const { data } = await APIAxiosInstance.get(`organisations${queryString}`)
      return data
    },
  }),
  getOrganisationAuthenticationConfig: ({
    organisationId,
  }: RequestTypes['getOrganisationAuthenticationConfig']) => ({
    queryKey: [organisationId],
    queryFn: async () => {
      const { data } = await APIAxiosInstance.get(
        `organisations/${organisationId}/mfa/configurations`,
      )
      return data
    },
  }),
})

export const organisationMutationKeys = createMutationKeys('organisation', {
  setOrganisationUserRole: {
    mutationKey: null,
    mutationFn: async (payload: RequestTypes['setOrganisationUserRole']) => {
      const { organisationId, userId, roles, companyId } = payload
      const { data } = await APIAxiosInstance.put(
        `organisations/${organisationId}/users/${userId}/roles`,
        {
          roles,
          ...(companyId && { companyId }),
        },
      )
      return data
    },
  },
  setOrganisationUserAuthGroups: {
    mutationKey: null,
    mutationFn: async (
      payload: RequestTypes['setOrganisationUserAuthGroups'],
    ) => {
      const { organisationId, userId, authGroupIds } = payload
      await APIAxiosInstance.put(
        `organisations/${organisationId}/users/${userId}/authorisation-groups`,
        {
          authorisationGroups: authGroupIds,
        },
      )
    },
  },
  setOrganisationUserStatus: {
    mutationKey: null,
    mutationFn: async (payload: RequestTypes['setOrganisationUserStatus']) => {
      const { organisationId, userId, status } = payload
      const { data } = await APIAxiosInstance.put(
        `organisations/${organisationId}/users/${userId}/status?status=${status}`,
      )
      return data
    },
  },
  inviteOrganisationUser: {
    mutationKey: null,
    mutationFn: async (payload: RequestTypes['inviteOrganisationUser']) => {
      const { organisationId } = payload
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/invites`,
        {
          ...payload,
        },
      )
      return data
    },
  },
  resendInvitation: {
    mutationKey: null,
    mutationFn: async (payload: RequestTypes['resendInvitation']) => {
      const { organisationId } = payload
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/resend-invitation`,
        {
          ...payload,
        },
      )
      return data
    },
  },
  updateOrganisation: {
    mutationKey: null,
    mutationFn: async (payload: RequestTypes['updateOrganisation']) => {
      const { organisationId } = payload
      const { data } = await APIAxiosInstance.put(
        `organisations/${organisationId}`,
        {
          ...payload,
        },
      )
      return data
    },
  },
  setOrganisationAuthenticationConfig: {
    mutationKey: null,
    mutationFn: async (
      payload: RequestTypes['setOrganisationAuthenticationConfig'],
    ) => {
      const { organisationId } = payload
      const { data } = await APIAxiosInstance.post(
        `organisations/${organisationId}/mfa/configurations`,
        {
          ...payload,
        },
      )
      return data
    },
  },
  setOrganisationUserKiosk: {
    mutationKey: null,
    mutationFn: async (payload: RequestTypes['setOrganisationUserKiosk']) => {
      const { organisationId, userId, kioskMember } = payload
      const { data } = await APIAxiosInstance.put(
        `organisations/${organisationId}/users/${userId}/kiosk`,
        { kioskMember },
      )
      return data
    },
  },
  updateOrganisationFeatureFlags: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      featureFlags,
    }: RequestTypes['updateOrganisationFeatureFlags']) => {
      const { data } = await APIAxiosInstance.put(
        `organisations/${organisationId}/feature-flags`,
        {
          featureFlags,
        },
      )
      return data
    },
  },
  exportOrganisationUsersCsv: {
    mutationKey: null,
    mutationFn: async (payload: RequestTypes['exportOrganisationUsersCsv']) => {
      // createQueryKeyString serialises arrays as a single comma-separated param,
      // which is what the backend's Set<*> filters bind from.
      const queryString = createQueryKeyString(payload)
      const { data } = await APIAxiosInstance.get(
        `reporting/users/csv${queryString}`,
      )
      return data
    },
  },
})

export const organisationKeys = mergeQueryKeys(
  organisationQueryKeys,
  organisationMutationKeys,
).organisation
